import React from 'react';
import { Zap, RefreshCw, Thermometer, TrendingDown, ArrowRight } from 'lucide-react';

const IndustrialComparisonSection = () => {
  const metrics = [
    {
      id: 'charging',
      icon: Zap,
      multiIonValue: '15',
      multiIonUnit: 'min',
      multiIonLabel: 'Full Charge',
      lithiumValue: '4–8 hrs',
      advantage: '32× faster',
      whyItMatters: 'Charge during shift breaks instead of overnight. A single battery can now do the work of three in rotating operations, eliminating the need for swap stations and spare inventory.',
      accentColor: 'emerald',
    },
    {
      id: 'cycles',
      icon: RefreshCw,
      multiIonValue: '5,000',
      multiIonUnit: '+',
      multiIonLabel: 'Cycle Life',
      lithiumValue: '1,500–2,000',
      advantage: '2.5× longer',
      whyItMatters: 'At one cycle per day, this translates to 13+ years of operational life. For fleets, this means battery replacement intervals extend from 4–5 years to over a decade.',
      accentColor: 'cyan',
    },
    {
      id: 'temperature',
      icon: Thermometer,
      multiIonValue: '-40°',
      multiIonUnit: 'to 65°C',
      multiIonLabel: 'Operating Range',
      lithiumValue: '0° to 45°C',
      advantage: '3× wider range',
      whyItMatters: 'Deploy the same battery in cold storage warehouses (-30°C) and hot manufacturing floors (55°C+) without derating or climate-controlled enclosures.',
      accentColor: 'blue',
    },
    {
      id: 'tco',
      icon: TrendingDown,
      multiIonValue: '40',
      multiIonUnit: '%',
      multiIonLabel: 'Lower Lifetime Cost',
      lithiumValue: 'Higher TCO',
      advantage: 'vs Lithium-Ion',
      whyItMatters: 'Calculated over 10 years: fewer replacements, zero swap infrastructure, reduced maintenance, and no cooling systems. The upfront premium pays back within 18–24 months.',
      accentColor: 'amber',
    },
  ];

  const getAccentClasses = (color) => {
    const colors = {
      emerald: {
        text: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
        glow: 'shadow-emerald-500/20',
      },
      cyan: {
        text: 'text-cyan-400',
        bg: 'bg-cyan-500/10',
        border: 'border-cyan-500/20',
        glow: 'shadow-cyan-500/20',
      },
      blue: {
        text: 'text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/20',
        glow: 'shadow-blue-500/20',
      },
      green: {
        text: 'text-green-400',
        bg: 'bg-green-500/10',
        border: 'border-green-500/20',
        glow: 'shadow-green-500/20',
      },
      amber: {
        text: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/20',
        glow: 'shadow-amber-500/20',
      },
    };
    return colors[color] || colors.emerald;
  };

  return (
    <section className="py-24 bg-gray-950 relative overflow-hidden" data-testid="industrial-comparison-section" aria-labelledby="comparison-heading">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header with Image - SEO optimized */}
        <header className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-emerald-500" aria-hidden="true" />
                <span className="text-emerald-400 text-sm font-medium tracking-widest uppercase">
                  Performance Data
                </span>
              </div>
              
              <h2 id="comparison-heading" className="text-4xl md:text-5xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                Why Operations Teams Choose Multi-Ion Industrial Batteries
              </h2>
              
              <p className="text-gray-400 text-lg mb-4">
                Industrial battery decisions for forklifts, cranes, and warehouse mobility equipment are made on total cost of ownership, not sticker price. Here is how Multi-Ion traction battery technology compares to lithium-ion across the metrics that procurement and operations teams evaluate when selecting forklift batteries, crane batteries, and industrial energy storage systems.
              </p>
              <p className="text-gray-500 text-base">
                Each metric below includes the business impact—because specifications only matter when they translate to operational outcomes for your material handling fleet or backup power infrastructure.
              </p>
            </div>

            {/* Video */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 bg-gray-800">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  poster="https://images.pexels.com/videos/13422070/cargo-cargo-truck-carrier-company-13422070.jpeg?auto=compress&cs=tinysrgb&w=1200"
                >
                  <source src="https://videos.pexels.com/video-files/13422070/13422070-uhd_2560_1440_30fps.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </header>

        {/* Metrics Grid */}
        <div className="space-y-6" role="list" aria-label="Battery performance comparison metrics">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            const accent = getAccentClasses(metric.accentColor);
            
            return (
              <article 
                key={metric.id}
                className={`group relative bg-gray-900/50 border border-gray-800 rounded-2xl p-8 md:p-10 hover:border-gray-700 transition-all duration-300`}
                data-testid={`metric-${metric.id}`}
                role="listitem"
              >
                <div className="grid md:grid-cols-12 gap-8 items-center">
                  {/* Icon & Number - Takes visual dominance */}
                  <div className="md:col-span-5 flex items-center gap-6">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-16 h-16 rounded-xl ${accent.bg} ${accent.border} border flex items-center justify-center`} aria-hidden="true">
                      <Icon className={`w-8 h-8 ${accent.text}`} />
                    </div>
                    
                    {/* Big Number */}
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-5xl md:text-6xl lg:text-7xl font-black ${accent.text} tracking-tight`}>
                          {metric.multiIonValue}
                        </span>
                        <span className={`text-2xl md:text-3xl font-bold ${accent.text}`}>
                          {metric.multiIonUnit}
                        </span>
                      </div>
                      <h3 className="text-gray-400 text-sm uppercase tracking-wider mt-1">
                        {metric.multiIonLabel}
                      </h3>
                    </div>
                  </div>

                  {/* Comparison Badge */}
                  <div className="md:col-span-3">
                    <div className="flex flex-col gap-2">
                      {/* Advantage badge */}
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${accent.bg} ${accent.border} border w-fit`}>
                        <span className={`text-sm font-semibold ${accent.text}`}>
                          {metric.advantage}
                        </span>
                      </div>
                      {/* Lithium baseline */}
                      <div className="text-gray-500 text-sm">
                        <span className="text-gray-600">Lithium-Ion:</span>{' '}
                        <span className="text-gray-400">{metric.lithiumValue}</span>
                      </div>
                    </div>
                  </div>

                  {/* Why it matters */}
                  <div className="md:col-span-4">
                    <p className="text-gray-300 text-base leading-relaxed">
                      {metric.whyItMatters}
                    </p>
                  </div>
                </div>

                {/* Subtle index number */}
                <div className="absolute top-6 right-8 text-gray-800 text-6xl font-black opacity-50" aria-hidden="true">
                  0{index + 1}
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom Summary Bar */}
        <aside className="mt-16 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-emerald-500/10 border border-emerald-500/20 rounded-2xl p-8 md:p-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Industrial Batteries Built for Real Operating Conditions
              </h3>
              <p className="text-gray-400">
                Multi-Ion technology is engineered for the conditions your forklift fleets, crane operations, and warehouse mobility equipment actually face—not laboratory ideals. Whether you need traction batteries for cold storage, crane batteries for port operations, or backup power systems for critical infrastructure.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <a 
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-4 rounded-xl transition-all group"
                data-testid="comparison-cta-primary"
              >
                Request Technical Specs
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
              <a 
                href="/products"
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white font-medium px-8 py-4 rounded-xl border border-white/10 transition-all"
                data-testid="comparison-cta-secondary"
              >
                View Traction Battery Products
              </a>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default IndustrialComparisonSection;
